import {IWesleaConfig} from "@/modules/perRoad/interfaces/monteCarlo";
import {
  IPavementStructureScenarioImperial,
  IPavementStructureScenarioMetric
} from "@/modules/structure/interfaces/pavementStructure";
import {UnitSystem} from "@/interfaces/units";
import {structureScenarioMetricToImperial} from "@/modules/structure/lib/unitConversion";

const uuid = require("uuid");

const CHUNKSIZE = 10;

export const createGridConfig = (s: IPavementStructureScenarioImperial, numPoints = 2): IWesleaConfig[] => {
  const subgradeLayer = s.structureLayers[s.structureLayers.length - 1];
  const criticallocations: { i: number | string, l: number, x: number, y: number, z: number }[] = [];

  /**
   * Create grid of crit locations
   */

  let ymax = -1000;
  let ymin = 1000;
  let xmax = -1000;
  let xmin = 1000;

  // Get bounds of loading locations
  s.loads.forEach(load => {
    ymax = load.location_in.y > ymax ? load.location_in.y : ymax;
    xmax = load.location_in.x > xmax ? load.location_in.x : xmax;
    ymin = load.location_in.y < ymin ? load.location_in.y : ymin;
    xmin = load.location_in.x < xmin ? load.location_in.x : xmin;
  });

  const loadingWidth = xmax - xmin;
  const loadingLength = ymax - ymin;

  const patternBufferScale = 1;

  const numPointsY = numPoints;
  const numPointsX = numPointsY * 0.5;

  const yPoints: number[] = [];
  const xPoints: number[] = [];

  const yInterval = loadingLength / numPointsY;
  const xInterval = loadingWidth / numPointsX;

  for (let x = xmin - (xInterval * patternBufferScale); x <= xmax + (xInterval * patternBufferScale); x += xInterval) {
    xPoints.push(x);
  }

  for (let y = ymin - (yInterval * patternBufferScale); y <= ymax + (yInterval * patternBufferScale); y += yInterval) {
    yPoints.push(y);
  }

  let i = 0;
  xPoints.forEach((x, n) => {
    yPoints.forEach((y, m) => {
      let depthOfLayerTop = 0;
      let depthOfLayerBottom = 0;
      s.structureLayers.forEach((layer, k) => {

          // Only generate points for layers that arent "infinity" thick, or what passess for it in weslea
          if ((layer.thickness_in.mean || 1000) < 100) {
            depthOfLayerBottom += layer.thickness_in.mean || 0;
            criticallocations.push({
              i: uuid.v4(),
              l: k + 1,
              x: x,
              y: y,
              z: depthOfLayerTop
            });
            criticallocations.push({
              i: uuid.v4(),
              l: k + 1,
              x: x,
              y: y,
              z: depthOfLayerBottom
            });
            i += 1;
          }
          depthOfLayerTop += layer.thickness_in.mean || 0;
        }
      );
    });
  });


  /**
   * Create a config for each set of points so we can run them all given welsea's limitation of 50 crit points per run
   */

  const configs: IWesleaConfig[] = [];

  const chunkSize = CHUNKSIZE;

  let n = 0;

  for (let c = 0; c < criticallocations.length; c += chunkSize) {
    const critLocsToUse = criticallocations.slice(c, c + chunkSize);

    // const configId = String(n);
    const configId = uuid.v4();
    n += 1;

    configs.push({
        drawId: configId,
        layers: s.structureLayers.map((L) => (
          {modulus: L.modulus_psi[0].mean || 0, poissons: L.poissonRatio, thickness: L.thickness_in.mean || 0}
        )),
        loads: s.loads.map((load) => (
          {
            pressure: load.tirePressure_psi,
            radius: Number(Math.sqrt(load.load_lbf / (Math.PI * load.tirePressure_psi)).toFixed(2)),
            x: load.location_in.x,
            y: load.location_in.y,
          }
        )),
        criticallocations: critLocsToUse,
        subgrademodulus: subgradeLayer.modulus_psi[0].mean || 0,
        subgradepoissons: subgradeLayer.poissonRatio,
        layerinterface: [1, 1, 1],
      }
    );

  }

  return configs;
};


export const createLinesConfig = (s: IPavementStructureScenarioImperial, numPoints = 50): IWesleaConfig[] => {
  const subgradeLayer = s.structureLayers[s.structureLayers.length - 1];
  const criticallocations: { i: number | string, l: number, x: number, y: number, z: number }[] = [];

  /**
   * Create grid of crit locations
   */

  let ymax = -1000;
  let ymin = 1000;
  let xmax = -1000;
  let xmin = 1000;

  // Get bounds of loading locations
  s.loads.forEach(load => {
    ymax = load.location_in.y > ymax ? load.location_in.y : ymax;
    xmax = load.location_in.x > xmax ? load.location_in.x : xmax;
    ymin = load.location_in.y < ymin ? load.location_in.y : ymin;
    xmin = load.location_in.x < xmin ? load.location_in.x : xmin;
  });

  const loadingWidth = xmax - xmin;
  const loadingLength = ymax - ymin;

  const patternBufferScale = 5;

  const numPointsY = numPoints;
  const numPointsX = numPointsY * 0.5;

  const yPoints: number[] = [];
  const xPoints: number[] = [];

  const yInterval = loadingLength / numPointsY;
  const xInterval = loadingWidth / numPointsX;

  for (let x = xmin - (xInterval * patternBufferScale); x <= xmax + (xInterval * patternBufferScale); x += xInterval) {
    xPoints.push(x);
  }

  for (let y = ymin - (yInterval * patternBufferScale); y <= ymax + (yInterval * patternBufferScale); y += yInterval) {
    yPoints.push(y);
  }

  let i = 0;

  xPoints.forEach((x, n) => {
    let depthOfLayerTop = 0;
    let depthOfLayerBottom = 0;
    s.structureLayers.forEach((layer, k) => {

        // Only generate points for layers that arent "infinity" thick, or what passess for it in weslea
        if ((depthOfLayerTop) < 100) {
          depthOfLayerBottom += layer.thickness_in.mean || 0;
          criticallocations.push({
            i: uuid.v4(),
            l: k + 1,
            x: x,
            y: 0,
            z: depthOfLayerTop
          });
          criticallocations.push({
            i: uuid.v4(),
            l: k + 1,
            x: x,
            y: 0,
            z: depthOfLayerBottom
          });
          i += 1;
        }
        depthOfLayerTop += layer.thickness_in.mean || 0;
      }
    );
  });

  yPoints.forEach((y, m) => {
    let depthOfLayerTop = 0;
    let depthOfLayerBottom = 0;
    s.structureLayers.forEach((layer, k) => {

        // Only generate points for layers that arent "infinity" thick, or what passess for it in weslea
        if ((depthOfLayerTop) < 100) {
          depthOfLayerBottom += layer.thickness_in.mean || 0;
          criticallocations.push({
            i: uuid.v4(),
            l: k + 1,
            x: 0,
            y: y,
            z: depthOfLayerTop
          });
          criticallocations.push({
            i: uuid.v4(),
            l: k + 1,
            x: 0,
            y: y,
            z: depthOfLayerBottom
          });
          i += 1;
        }
        depthOfLayerTop += layer.thickness_in.mean || 0;
      }
    );
  });


  /**
   * Create a config for each set of points so we can run them all given welsea's limitation of 50 crit points per run
   */

  const configs: IWesleaConfig[] = [];

  const chunkSize = CHUNKSIZE;

  let n = 0;

  for (let c = 0; c < criticallocations.length; c += chunkSize) {
    const critLocsToUse = criticallocations.slice(c, c + chunkSize);

    // const configId = String(n);
    const configId = uuid.v4();
    n += 1;

    configs.push({
      drawId: configId,
      layers: s.structureLayers.map((L) => (
        {modulus: L.modulus_psi[0].mean || 0, poissons: L.poissonRatio, thickness: L.thickness_in.mean || 0}
      )),
      loads: s.loads.map((load) => (
        {
          pressure: load.tirePressure_psi,
          radius: Number(Math.sqrt(load.load_lbf / (Math.PI * load.tirePressure_psi)).toFixed(2)),
          x: load.location_in.x,
          y: load.location_in.y,
        }
      )),
      criticallocations: critLocsToUse,
      subgrademodulus: subgradeLayer.modulus_psi[0].mean || 0,
      subgradepoissons: subgradeLayer.poissonRatio,
      layerinterface: [1, 1, 1],
    });

  }

  return configs;
};

export const setCurrentResponseConfig = (scenario: IPavementStructureScenarioImperial | IPavementStructureScenarioMetric) => {
  /***
   * Automatically build the weslea configuration based on the current scenario response config. Keeps us from having
   * to save these deterministic elements to the DB.
   */
  let wesleaConfigs: IWesleaConfig[];
  if (scenario.responsePattern === "grid") {
    if (scenario.unitSystem === UnitSystem.Imperial) {
      wesleaConfigs = createGridConfig(scenario, scenario.numGridPoints);
    } else {
      wesleaConfigs = createGridConfig(structureScenarioMetricToImperial(scenario), scenario.numGridPoints);
    }
  } else {
    if (scenario.unitSystem === UnitSystem.Imperial) {
      wesleaConfigs = createLinesConfig(scenario, scenario.numLinePoints);
    } else {
      wesleaConfigs = createLinesConfig(structureScenarioMetricToImperial(scenario), scenario.numLinePoints);
    }
  }
  return wesleaConfigs;
};


export const combineWesleaConfigSet = (configs: IWesleaConfig[], layerNumber: number | null = null): IWesleaConfig => {
  const allConfigs = configs;
  const combinedConfig: IWesleaConfig = {...allConfigs[0]};
  combinedConfig.criticallocations = [];
  allConfigs.forEach((config, i) => {
    if (layerNumber !== null) {
      const layerCritLocs = config.criticallocations.filter(cr => cr.l === layerNumber);

      combinedConfig.criticallocations.push(...layerCritLocs);
    } else {
      combinedConfig.criticallocations.push(...config.criticallocations);
    }
  });
  return combinedConfig;
};

